import React, { useCallback, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import {
  deepLinkToWebPath,
  detectPlatform,
  getPendingDeepLink,
  redirectToAppStore,
  storePendingDeepLink,
} from './DeepLinkUtils';
import { useDeepLinkStore } from '../store/deepLink.store';

export interface DeepLinkPageProps {
  title: string;
  description?: string;
  deepLink: string;
  iosStoreUrl?: string;
  androidStoreUrl?: string;
  showDownloadButtons?: boolean;
  extraContent?: React.ReactNode;
}

import { IOS_APP_STORE_URL, ANDROID_PLAY_STORE_URL, IOS_APP_STORE_ID, MOBILE_APP_PAUSED } from '../constants/appStore';

const defaultAndroidStoreUrl = ANDROID_PLAY_STORE_URL;
const defaultIosStoreUrl = IOS_APP_STORE_URL;

/**
 * Phase 1 alternative view rendered when MOBILE_APP_PAUSED is true. Replaces
 * the "Open in App" + App Store / Play Store buttons (which would dead-end
 * since the apps are delisted) with a single "Continue on the web" CTA that
 * routes to the web equivalent of the deep link. Extracted as its own
 * component so each branch keeps a clean hook order.
 */
function PausedDeepLinkView({
  title,
  description,
  deepLink,
  extraContent,
}: Pick<DeepLinkPageProps, 'title' | 'description' | 'deepLink' | 'extraContent'>) {
  const navigate = useNavigate();
  const webPath = deepLinkToWebPath(deepLink);

  return (
    <div className="min-h-screen bg-[#111010] flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 font-['Inter']">
      <Helmet>
        <title>{title}</title>
        <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
      </Helmet>

      <div className="w-full max-w-md flex flex-col items-center">
        <div className="flex items-center space-x-4 mb-12">
          <img
            src="/rollops-logo.png"
            alt="RollOps Logo"
            className="w-24 h-24 sm:w-32 sm:h-32"
          />
          <span className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#EBFFEE]">
            Roll<span className="text-rollops-accent">O</span>ps
          </span>
        </div>

        <Card className="w-full border-[#504A4A] bg-[#221F1F] shadow-lg">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-bold text-center text-[#EBFFEE]">
              {title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {description ? (
              <p className="text-base text-[#A9A3A3] leading-relaxed text-center">
                {description}
              </p>
            ) : null}

            <div className="rounded-lg border border-[#504A4A] bg-[#1a1717] p-4 text-sm text-[#A9A3A3]">
              The RollOps mobile app is temporarily paused while we focus on stability.
              Continue here in your browser — everything works the same.
            </div>

            <Button
              onClick={() => navigate(webPath)}
              variant="red"
              size="lg"
              className="w-full font-semibold"
            >
              Continue on the web
            </Button>

            {extraContent ? <div className="pt-4">{extraContent}</div> : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function DeepLinkPage(props: DeepLinkPageProps) {
  if (MOBILE_APP_PAUSED) {
    return (
      <PausedDeepLinkView
        title={props.title}
        description={props.description}
        deepLink={props.deepLink}
        extraContent={props.extraContent}
      />
    );
  }
  return <ActiveDeepLinkPage {...props} />;
}

function ActiveDeepLinkPage({
  title,
  description,
  deepLink,
  iosStoreUrl = defaultIosStoreUrl,
  androidStoreUrl = defaultAndroidStoreUrl,
  showDownloadButtons = true,
  extraContent,
}: DeepLinkPageProps) {
  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const platform = detectPlatform();
  const hasCheckedRef = useRef(false);

  // Use Zustand store for app detection state
  const {
    isCheckingApp,
    appInstalled,
    checkAppAvailability,
    setDeepLinkUrl
  } = useDeepLinkStore();

  // Set deep link URL in store on mount
  useEffect(() => {
    setDeepLinkUrl(deepLink);
  }, [deepLink, setDeepLinkUrl]);

  // Check for pending deep link on mount (user returned from App Store)
  useEffect(() => {
    if (isLocalhost) return;
    
    const pendingLink = getPendingDeepLink();
    
    if (pendingLink && pendingLink === deepLink) {
      // User returned from App Store, check if app is now installed
      checkAppAvailability(deepLink)
        .then((installed) => {
          if (installed) {
            // App is now installed! Open it
            setTimeout(() => {
              try {
                window.location.href = deepLink;
              } catch {
                // noop
              }
            }, 500); // Small delay to ensure page is ready
          } else {
            // Still not installed, store again
            storePendingDeepLink(deepLink);
          }
        })
        .catch(() => {
          storePendingDeepLink(deepLink);
        });
    }
  }, [deepLink, isLocalhost, checkAppAvailability]);

  // Auto-detect app installation on mount (if no pending link)
  // Only check in background - don't auto-redirect to prevent error popups
  useEffect(() => {
    if (hasCheckedRef.current || isLocalhost) return;
    
    const pendingLink = getPendingDeepLink();
    
    // If there's a pending link, the effect above will handle it
    if (pendingLink) return;
    
    hasCheckedRef.current = true;

    // Check app availability in background without redirecting
    // This prevents error popups if app is not installed
    checkAppAvailability(deepLink)
      .then((installed) => {
        // Don't auto-redirect - let user click button to open
        // This prevents error popups
        if (!installed) {
          // App not installed, store the deep link for after installation
          storePendingDeepLink(deepLink);
        }
      })
      .catch(() => {
        storePendingDeepLink(deepLink);
      });
  }, [deepLink, isLocalhost, checkAppAvailability]);

  const handleOpen = useCallback(async () => {
    if (isLocalhost) {
      // In localhost/simulator, try to open directly immediately
      // On iOS simulator, this should work if the app is running
      try {
        // Try opening the deep link
        window.location.href = deepLink;
        
        // Also try with a small delay as fallback
        setTimeout(() => {
          try {
            window.location.href = deepLink;
          } catch {
            // noop
          }
        }, 100);
      } catch {
        // noop
      }
      return;
    }

    // Check if app is installed using store
    const installed = await checkAppAvailability(deepLink);

    if (installed) {
      // App is installed, open it
      try {
        window.location.href = deepLink;
      } catch {
        // noop
      }
    } else {
      // App not installed, redirect to App Store
      redirectToAppStore(deepLink, platform);
    }
  }, [deepLink, isLocalhost, platform, checkAppAvailability]);

  const handleDownload = useCallback(() => {
    storePendingDeepLink(deepLink);
    const storeUrl = platform === 'ios' ? iosStoreUrl : platform === 'android' ? androidStoreUrl : iosStoreUrl;
    if (storeUrl) {
      window.open(storeUrl, '_blank');
    }
  }, [deepLink, platform, iosStoreUrl, androidStoreUrl]);

  return (
    <div className="min-h-screen bg-[#111010] flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 font-['Inter']">
        <Helmet>
          <title>{title}</title>
          <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
          <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
          <meta name="bingbot" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
          {/* iOS Smart App Banner */}
          {iosStoreUrl && (
            <meta name="apple-itunes-app" content={`app-id=${IOS_APP_STORE_ID}, app-argument=${encodeURIComponent(deepLink)}`} />
          )}
        </Helmet>
        
        <div className="w-full max-w-md flex flex-col items-center">
          {/* Logo and Brand */}
          <div className="flex items-center space-x-4 mb-12">
            <img 
              src="/rollops-logo.png" 
              alt="RollOps Logo" 
              className="w-24 h-24 sm:w-32 sm:h-32"
            />
            <span className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#EBFFEE]">
              Roll<span className="text-rollops-accent">O</span>ps
            </span>
          </div>

          {/* Main Card */}
          <Card className="w-full border-[#504A4A] bg-[#221F1F] shadow-lg">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl font-bold text-center text-[#EBFFEE]">
                {title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {description ? (
                <p className="text-base text-[#A9A3A3] leading-relaxed text-center">
                  {description}
                </p>
              ) : null}

              <div className="space-y-4">
                <Button
                  onClick={handleOpen}
                  disabled={isCheckingApp}
                  variant="red"
                  size="lg"
                  className="w-full font-semibold"
                >
                  {isCheckingApp ? 'Checking...' : appInstalled === false ? 'Install & Open RollOps' : 'Open RollOps'}
                </Button>

                {showDownloadButtons ? (
                  <div className="flex flex-col gap-3">
                    {/* Show platform-specific store button prominently */}
                    {platform === 'ios' ? (
                      <Button
                        onClick={handleDownload}
                        variant="outline"
                        className="w-full border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent py-4 px-6 flex items-center justify-center gap-3"
                      >
                        <img 
                          src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1704067200" 
                          alt="Download on the App Store"
                          className="h-12 w-auto"
                          onError={(e) => {
                            // Fallback if image fails to load
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <span className="sr-only">Download for iOS</span>
                      </Button>
                    ) : platform === 'android' ? (
                      <Button
                        onClick={handleDownload}
                        variant="outline"
                        className="w-full border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent py-4 px-6 flex items-center justify-center gap-3"
                      >
                        <img 
                          src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" 
                          alt="Get it on Google Play"
                          className="h-16 w-auto"
                          onError={(e) => {
                            // Fallback if image fails to load
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <span className="sr-only">Download for Android</span>
                      </Button>
                    ) : (
                      // Desktop - show both options
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button
                          onClick={() => {
                            storePendingDeepLink(deepLink);
                            window.open(iosStoreUrl, '_blank');
                          }}
                          variant="outline"
                          className="flex-1 border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent py-4 px-6 flex items-center justify-center"
                        >
                          <img 
                            src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1704067200" 
                            alt="Download on the App Store"
                            className="h-12 w-auto"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </Button>
                        <Button
                          onClick={() => {
                            storePendingDeepLink(deepLink);
                            window.open(androidStoreUrl, '_blank');
                          }}
                          variant="outline"
                          className="flex-1 border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent py-4 px-6 flex items-center justify-center"
                        >
                          <img 
                            src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" 
                            alt="Get it on Google Play"
                            className="h-16 w-auto"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </Button>
                      </div>
                    )}
                    
                    {/* Fallback text buttons if images don't load */}
                    {platform !== 'desktop' && (
                      <div className="flex flex-col sm:flex-row gap-3">
                        {platform !== 'android' && (
                          <Button
                            onClick={() => window.open(androidStoreUrl, '_blank')}
                            variant="outline"
                            className="flex-1 border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent py-3 px-6"
                          >
                            Download for Android
                          </Button>
                        )}
                        {platform !== 'ios' && (
                          <Button
                            onClick={() =>
                              iosStoreUrl
                                ? window.open(iosStoreUrl, '_blank')
                                : window.alert('iOS build is pending App Store approval.')
                            }
                            variant="outline"
                            disabled={!iosStoreUrl}
                            className="flex-1 border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] hover:text-[#EBFFEE] bg-transparent disabled:opacity-65 py-3 px-6"
                          >
                            {iosStoreUrl ? 'Download for iOS' : 'iOS build coming soon'}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ) : null}

                {appInstalled === false ? (
                  <p className="text-sm text-[#A9A3A3] text-center">
                    After installing, return here and tap &quot;Open RollOps&quot; again to continue.
                  </p>
                ) : (
                  <p className="text-sm text-[#A9A3A3] text-center">
                    App didn&apos;t open? Make sure RollOps is installed, then tap &quot;Open RollOps&quot; again.
                  </p>
                )}
              </div>

              {/* DEV ONLY: Show copyable deep link and terminal command */}
              {isLocalhost && (
                <div className="mt-6 p-4 bg-[#1a1717] border border-[#504A4A] rounded-lg">
                  <p className="text-xs font-mono text-rollops-accent mb-2">🛠️ DEV MODE - Copy for Simulator:</p>
                  
                  {/* Terminal command for xcrun */}
                  <div className="mb-3">
                    <p className="text-xs text-[#A9A3A3] mb-1">iOS Simulator command:</p>
                    <div className="relative group">
                      <pre className="text-xs font-mono text-[#EBFFEE] bg-[#111010] p-2 rounded overflow-x-auto whitespace-pre-wrap break-all">
                        {`xcrun simctl openurl booted "${deepLink}"`}
                      </pre>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`xcrun simctl openurl booted "${deepLink}"`);
                          alert('Copied terminal command!');
                        }}
                        className="absolute top-1 right-1 px-2 py-1 text-xs bg-[#9B1421] text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  {/* Raw deep link */}
                  <div>
                    <p className="text-xs text-[#A9A3A3] mb-1">Deep link URL:</p>
                    <div className="relative group">
                      <pre className="text-xs font-mono text-[#EBFFEE] bg-[#111010] p-2 rounded overflow-x-auto whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
                        {deepLink}
                      </pre>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(deepLink);
                          alert('Copied deep link!');
                        }}
                        className="absolute top-1 right-1 px-2 py-1 text-xs bg-[#9B1421] text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {extraContent ? <div className="pt-4">{extraContent}</div> : null}
            </CardContent>
          </Card>
        </div>
      </div>
  );
}

export default DeepLinkPage;
